import { getAuth } from "firebase/auth";
import CryptoJS from "crypto-js";

import store from "../../redux/store";
import { setUserToken } from "../../redux/slices/userSlice";
import { server } from "../../config/ConfigFirebase";

const handleUploadFile = async (user, formData, retry) => {
  // Un fallo de red (la función corta la conexión mientras el cuerpo todavía
  // sube, CORS, el usuario sin conexión) hace que fetch RECHACE, no que
  // devuelva una respuesta. Sin este catch la excepción sube hasta el
  // componente que carga el archivo y revienta la pantalla con "Failed to
  // fetch"; con él, el fallo se convierte en el null que ya manejan todas las
  // pantallas que suben archivos, con su mensaje en español.
  let response;
  try {
    response = await fetch("https://" + server + ".cloudfunctions.net/handleUploadFile", {
      method: "post",
      body: formData,
      headers: {
        Authorization: "Bearer " + user.token,
      },
    });
  } catch (error) {
    console.error("handleUploadFile: la petición falló", error);
    return null;
  }

  if (response.status === 401 && !retry) {
    const auth = getAuth();
    const token = await auth.currentUser.getIdToken();
    store.dispatch(setUserToken(token));
    return await handleUploadFile(user, formData, true);
  }

  return response;
};

// Códigos con los que el servidor rechaza escribir en la carpeta de un radicado
// formalizado o anulado (filedLock.js en las funciones de carga).
export const FILED_LOCKED = "FILED_LOCKED";
export const FILED_STATUS_UNAVAILABLE = "FILED_STATUS_UNAVAILABLE";
const REFUSAL_CODES = [FILED_LOCKED, FILED_STATUS_UNAVAILABLE];

// { result:false, code, error } cuando la respuesta es un rechazo por radicado
// bloqueado (409) o sin estado verificable (503); null en cualquier otro caso.
const readRefusal = async (response) => {
  if (!response || (response.status !== 409 && response.status !== 503)) return null;
  try {
    const body = await response.json();
    if (body && REFUSAL_CODES.includes(body.code)) {
      return { result: false, code: body.code, error: body.error };
    }
  } catch (error) {
    // Cuerpo no JSON: no es un rechazo de bloqueo.
  }
  return null;
};

// Helper function to calculate MD5 hash of a file
const calculateFileHash = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        const wordArray = CryptoJS.lib.WordArray.create(event.target.result);
        const hash = CryptoJS.MD5(wordArray).toString();
        resolve(hash);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Error al leer el archivo'));
    };
    
    reader.readAsArrayBuffer(file);
  });
};

// Headers for the PUT to a signed upload URL: the file's Content-Type plus the
// headers handlesigneduploadurl signed (x-goog-acl: public-read, so the object is
// born public like everything handleUploadFile uploads). Every signed header must
// arrive or Storage rejects the signature, and none may be added that was not
// signed, so an older service that returns no headers gets only Content-Type.
export const signedPutHeaders = (file, signedHeaders) => {
  const headers = { 'Content-Type': file.type || 'application/octet-stream' };
  if (signedHeaders && typeof signedHeaders === 'object') {
    Object.entries(signedHeaders).forEach(([name, value]) => {
      if (name.toLowerCase() !== 'content-type' && typeof value === 'string') headers[name] = value;
    });
  }
  return headers;
};

// Upload file using signed URL (for files > 32MB)
//
// The PUT in step 2 goes from the BROWSER straight to storage.googleapis.com, so
// it is cross-origin and preflighted: the destination bucket's own CORS policy
// must allow PUT from this origin. The Cloud Function is not in the loop, so its
// CORS headers do not help here.
//
// A bucket missing that rule fails with "No 'Access-Control-Allow-Origin' header
// is present" while files <= 32MB keep working (they go through the function),
// which reads like a file-size bug but is not. Bucket CORS lives in GCP, not in
// the repo, so it does not travel with a deploy — apply it with
// scripts/apply-storage-cors.sh after creating or renaming any bucket. The PUT
// also sends x-goog-acl, which the policy must allow or the preflight fails.
const uploadWithSignedUrl = async (user, file, bucket, filePath, { returnRefusal = false } = {}) => {
  try {
    // 1. Request signed URL from Cloud Function
    const signedUrlResponse = await fetch(
      `https://handlesigneduploadurl-${process.env.REACT_APP_RUN_ID}.us-east1.run.app`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({
          bucket,
          filePath,
          contentType: file.type || 'application/octet-stream',
          publicRead: true
        })
      });

    if (!signedUrlResponse.ok) {
      console.error('Error al obtener URL firmada');
      return returnRefusal ? await readRefusal(signedUrlResponse) : null;
    }

    const { signedUrl, headers } = await signedUrlResponse.json();

    // 2. Upload file directly to Google Cloud Storage using signed URL
    const uploadResponse = await fetch(signedUrl, {
      method: 'PUT',
      body: file,
      headers: signedPutHeaders(file, headers)
    });

    if (!uploadResponse.ok) {
      console.error('Error al cargar archivo');
      return null;
    }

    // 3. Calculate file hash
    const fileHash = await calculateFileHash(file);

    // 4. Return response in the same format as handleUploadFile
    const downloadUrl = `https://storage.googleapis.com/${bucket}/${filePath}`;
    
    return {
      result: true,
      downloadUrl,
      fileHash
    };
  } catch (error) {
    console.error('Error en uploadWithSignedUrl:', error);
    return null;
  }
};

// Devuelve la respuesta del servidor, o null si la carga falla. Con
// { returnRefusal: true } (las pantallas de radicados), un rechazo por radicado
// formalizado o anulado vuelve como { result:false, code, error } para mostrar
// su mensaje. Sin la opción el contrato sigue siendo null, porque hay llamadores
// que solo revisan `if (!response)` y luego leen downloadUrl.
export const UploadFile = async (
  user,
  file,
  bucket,
  filePath,
  makeDocumentAI,
  appends,
  { returnRefusal = false } = {}
) => {
  // Check file size - use signed URL for files > 32MB
  const FILE_SIZE_LIMIT = 32 * 1024 * 1024; // 32MB
  
  if (file.size > FILE_SIZE_LIMIT) {
    // Use signed URL for large files
    return await uploadWithSignedUrl(user, file, bucket, filePath, { returnRefusal });
  }
  
  // Use Cloud Function for files <= 32MB
  const formData = new FormData();
  formData.append("file", file);
  formData.append("bucket", bucket);
  formData.append("filePath", filePath);

  if (appends) {
    Object.keys(appends).forEach((key) => {
      formData.append(key, appends[key]);
    });
  }

  if (makeDocumentAI) formData.append("makeDocumentAI", "true");
  const response = await handleUploadFile(user, formData);
  if (response && response.status === 200) return await response.json();
  return returnRefusal ? await readRefusal(response) : null;
};

export const DeleteFile = async (
  user,
  bucket,
  filePath,
  { returnRefusal = false } = {}
) => {
  const formData = new FormData();
  formData.append("action", "delete");
  formData.append("bucket", bucket);
  formData.append("filePath", filePath);
  const response = await handleUploadFile(user, formData);
  if (response && response.status === 200) return await response.json();
  return returnRefusal ? await readRefusal(response) : null;
};
